import {Circle, LayersControl, MapContainer, Marker, Popup, TileLayer, useMap, useMapEvent} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import React, {useEffect} from "react";
import LocalStorage from "../../../../../../util/localStorage";

export default function MapCanvas({
                                      center,
                                      zoom = 13,
                                      onClick,
                                      areMarkerClustered,
                                      searchRadius,
                                      data,
                                      fitBoundsRef,
                                      onGetCustomIcon
                                  }) {
    let markers = Array.isArray(data) ? data : [data];
    const userSelectedLayer = LocalStorage.get("user_map_settings")?.selectedLayer ?? "Default";

    markers = markers.filter(marker => !!marker?.Latitude && !!marker?.Longitude);
    const fallbackCenter = [markers?.[0]?.Latitude, markers?.[0]?.Longitude];
    const mapCenter = center ?? fallbackCenter;

    const layers = [
        {
            name: "Default",
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            attribution: "&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
        },
        {
            name: "Dark mode",
            url: "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
            attribution: "&copy; <a href=\"http://www.openstreetmap.org/copyright\">OpenStreetMap</a> &copy; <a href=\"http://cartodb.com/attributions\">CartoDB</a>"
        },
        {
            name: "Satellite",
            url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        },
        {
            name: "Google-Map",
            url: "https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}",
            attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        },
        {
            name: "Google-Hybrid",
            url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
            attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        }
    ];

    return (
        <MapContainer
            center={mapCenter}
            zoom={zoom}
            style={{height: '100%', width: '100%'}}
        >
            <ChangeMapView center={mapCenter} zoom={zoom}/>

            {!!fitBoundsRef && (
                <FitMapBounds markers={markers} fitBoundsRef={fitBoundsRef}/>
            )}

            <LayersControl position="topright">
                {layers.map(layer => (
                    <LayersControl.BaseLayer
                        key={layer.name}
                        checked={layer.name === userSelectedLayer}
                        name={layer.name}
                    >
                        <TileLayer
                            url={layer.url}
                            attribution={layer.attribution}
                        />
                    </LayersControl.BaseLayer>
                ))}
            </LayersControl>

            {!areMarkerClustered && !!markers.length && markers.map((marker, index) => (
                <Marker
                    key={index}
                    position={[marker.Latitude, marker.Longitude]}
                    {...(onGetCustomIcon ? { icon: onGetCustomIcon(marker) } : {})}
                >
                    {marker.label && <Popup>{marker.label}</Popup>}
                </Marker>
            ))}

            {areMarkerClustered && !!markers.length && (
                <MarkerClusterGroup>
                    {markers.map((marker, index) => (
                        <Marker
                            key={index}
                            position={[marker.Latitude, marker.Longitude]}
                            {...(onGetCustomIcon ? { icon: onGetCustomIcon(marker) } : {})}
                        >
                            {marker.label && <Popup>{marker.label}</Popup>}
                        </Marker>
                    ))}
                </MarkerClusterGroup>
            )}

            {onClick && <MapClickHandler onClick={onClick}/>}

            {searchRadius?.center?.[0] && searchRadius?.center?.[1] && (
                <Circle
                    center={searchRadius.center}
                    radius={searchRadius.radius}
                />
            )}

            <LayerChangeHandler layers={layers}/>
        </MapContainer>
    );
}


function ChangeMapView({center, zoom}) {
    const map = useMap();

    useEffect(() => {
        if (center && zoom !== undefined) {
            map.setView(center, zoom);
        }
    }, [center, zoom, map]);

    return null;
}

function FitMapBounds({markers, fitBoundsRef}) {
    const map = useMap();

    if (fitBoundsRef) {
        fitBoundsRef.current = () => {
            const bounds = L.latLngBounds(
                markers.map(marker => [marker.Latitude, marker.Longitude])
            );
            if (bounds.isValid()) {
                map.fitBounds(bounds, {padding: [50, 50]});
            }
        };
    }

    return null;
}

function MapClickHandler({onClick}) {
    useMapEvent('click', (e) => {
        onClick(e);
    });
    return null;
}

function LayerChangeHandler({layers}) {
    useMapEvent('layeradd', (event) => {
        const layer = layers.find(l => l.url === event.layer._url);
        if (layer) {
            LocalStorage.set("user_map_settings", {selectedLayer: layer.name});
        }
    });
    return null;
}